// Pure derivation helpers + shared types for the per-work-order Labels + Stars
// feature.
//
// Display state is read directly from the work order JSON (`workOrder.labels` /
// `workOrder.stars`, both indexed server-side and present in the search + detail
// responses). After a mutation the calling component persists via the API thunk
// and dispatches the generated WORK_ORDERS updatePartialSuccess action to merge the
// new stars/labels into the cached work order, so every surface re-renders from Redux
// — no client-side cache needed.

import posthog from 'posthog-js';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';
import { retrieveCachedUserDetails } from '../thunks/session_thunks';

// Feature flag gating the entire Work Order Labels + Stars feature (track/untrack, label apply,
// label chips, the labels/starred filters, the Tracked tab, and the labels management page). When
// off, every star/label surface is hidden. Single source of the flag name.
export const isWorkOrderLabelsTrackEnabled = (): boolean =>
	!!posthog.isFeatureEnabled('workorder-labels-track');

// Mirrors the backend models (openwrench-app: WorkOrderLabel / WorkOrderUserStar).
export interface WorkOrderLabel {
	id: number;
	label: string;
	// Optional on the backend (Option[String]); always sent when created via the UI.
	color?: string;
	buyerCompanyId?: number;
	supplierFacilityId?: number;
	associatedWorkOrdersCount?: number;
}

// Per-user bookmark on a work order. The backend returns ONLY the current user's star in
// the search + detail payloads (other users' stars/notes are stripped server-side — see
// WorkOrderServiceImpl.getWorkOrderOSDocFromSource / hydrateWorkOrders). The email match in
// getWorkOrderStarState is just defensive selection of that entry, not the privacy boundary
// — client filtering must never be relied on to hide another user's private note.
export interface WorkOrderUserStar {
	email: string;
	workOrderId: number | string;
	starNote?: string | null;
	createdAt?: string;
	updatedAt?: string;
}

export type WorkOrderStarState = {
	starred: boolean;
	note: string | null;
};

export const currentUserEmail = (): string =>
	nullSafeGetOrElse('contact.email', retrieveCachedUserDetails(['contact']), '');

// --- Labels -----------------------------------------------------------------

export const getWorkOrderLabels = (workOrder: any): WorkOrderLabel[] =>
	nullSafeGetOrElse('labels', workOrder, []);

// Label add/remove is recorded as a work-order note, so it rides the existing WO
// notes WebSocket as a `new_note` event carrying `addedLabel` / `removedLabel` (the
// same fields MessageLog renders). This derives the `{ labels }` patch to merge into
// the cached work order so chips stay live on every open client — handled purely on
// the FE, mirroring how note display is. Returns null when the event is not a label
// change or is a no-op (already-present add / already-absent removal), making it safe
// against the acting client's own optimistic patch and re-delivered/echoed events.
export const getLabelSyncFromNoteEvent = (
	data: any,
	workOrder: any
): { labels: WorkOrderLabel[] } | null => {
	const addedLabel: WorkOrderLabel = nullSafeGet('addedLabel', data);
	const removedLabel: WorkOrderLabel = nullSafeGet('removedLabel', data);
	if (!addedLabel && !removedLabel) return null;
	const currentLabels = getWorkOrderLabels(workOrder);
	if (addedLabel) {
		if (currentLabels.some((label) => label.id === addedLabel.id)) return null;
		return { labels: [...currentLabels, addedLabel] };
	}
	if (!currentLabels.some((label) => label.id === removedLabel.id)) return null;
	return { labels: currentLabels.filter((label) => label.id !== removedLabel.id) };
};

// --- Stars ------------------------------------------------------------------

export const getWorkOrderStarState = (workOrder: any): WorkOrderStarState => {
	const stars: WorkOrderUserStar[] = nullSafeGetOrElse('stars', workOrder, []);
	const email = currentUserEmail();
	// Case-insensitive match: the backend matches stars with equalsIgnoreCase, so the
	// cached contact email and star.email can differ only by casing. A strict === would
	// then miss the user's own star and render it as unstarred with the note hidden.
	const mine = email ? stars.find((star) => star && compareStrings(star.email, email)) : undefined;
	return {
		starred: !!mine,
		note: mine ? nullSafeGet('starNote', mine) || null : null,
	};
};

export const isWorkOrderStarred = (workOrder: any): boolean =>
	getWorkOrderStarState(workOrder).starred;

export const getWorkOrderStarNote = (workOrder: any): string | null =>
	getWorkOrderStarState(workOrder).note;
